/**
 * Studio footer — warm, editorial, artistic. Not corporate.
 * Deep terracotta ground with a delicate bone-white typography layer.
 */
const currentYear = new Date().getFullYear();

const columns = [
  {
    title: "Studio",
    links: [
      { label: "About Elin", href: "#" },
      { label: "The Team", href: "#" },
      { label: "Press & Awards", href: "#" },
      { label: "Journal", href: "#" },
    ],
  },
  {
    title: "Work",
    links: [
      { label: "Hospitality", href: "#" },
      { label: "Residential", href: "#" },
      { label: "Cultural & Installations", href: "#" },
      { label: "Bespoke Fixtures", href: "#" },
    ],
  },
  {
    title: "Contact",
    links: [
      { label: "Book an appointment", href: "#book" },
      { label: "studio@elinvahr.com", href: "mailto:studio@elinvahr.com" },
      { label: "Copenhagen · +45 32 65 44 21", href: "tel:+4532654421" },
      { label: "Lisbon · +351 21 943 88 20", href: "tel:+351219438820" },
    ],
  },
];

const socials = [
  { label: "Instagram", href: "#", icon: "ri-instagram-line" },
  { label: "Are.na", href: "#", icon: "ri-book-line" },
  { label: "Pinterest", href: "#", icon: "ri-pinterest-line" },
  { label: "LinkedIn", href: "#", icon: "ri-linkedin-line" },
];

export default function StudioFooter() {
  return (
    <footer
      className="relative w-full overflow-hidden"
      style={{ backgroundColor: "#C56E3E", color: "#F5EFE4" }}
    >
      {/* Painterly ambient washes */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(60% 55% at 15% 20%, rgba(255,220,160,0.18) 0%, rgba(255,220,160,0) 60%), radial-gradient(70% 60% at 90% 90%, rgba(60,20,10,0.35) 0%, rgba(60,20,10,0) 60%)",
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.10]"
        style={{
          backgroundImage:
            "radial-gradient(rgba(0,0,0,0.4) 1px, transparent 1px)",
          backgroundSize: "3px 3px",
        }}
      />

      <div className="relative z-10 max-w-[1280px] mx-auto px-6 md:px-10 lg:px-14 pt-24 md:pt-32 pb-10">
        {/* Massive wordmark */}
        <div className="border-b border-[#F5EFE4]/25 pb-14 md:pb-20">
          <div className="flex items-center gap-3 mb-8">
            <span className="w-2.5 h-2.5 rounded-full bg-[#F5EFE4] animate-pulse" />
            <span className="text-[10px] uppercase tracking-[0.4em] text-[#F5EFE4]/80 whitespace-nowrap">
              Elin Vahr · Lighting Design Studio
            </span>
          </div>
          <div
            className="font-black uppercase leading-[0.82] tracking-[-0.04em]"
            style={{ fontSize: "clamp(60px, 13vw, 220px)" }}
          >
            DRAW WITH
            <br />
            <span className="italic font-light lowercase tracking-tight text-[#F5EFE4]/70">
              us in the
            </span>{" "}
            LIGHT.
          </div>
          <div className="mt-10 md:mt-14 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <p className="max-w-md text-sm md:text-base text-[#F5EFE4]/85 leading-relaxed">
              A small independent lighting studio designing fixtures and
              atmospheres for the hours after sunset — since 2018, from
              Copenhagen and Lisbon.
            </p>
            <a
              href="#book"
              className="group inline-flex items-center justify-center gap-3 bg-[#F5EFE4] text-[#1B1815] hover:bg-white text-[13px] uppercase tracking-[0.3em] px-8 py-4 rounded-full cursor-pointer transition-colors whitespace-nowrap self-start md:self-auto"
            >
              Book an appointment
              <i className="ri-arrow-right-up-line text-[16px] transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </div>
        </div>

        {/* Link columns */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10 md:gap-8 py-14 md:py-20">
          <div>
            <div className="text-[10px] uppercase tracking-[0.4em] text-[#F5EFE4]/70 mb-5">
              Est. 2018
            </div>
            <p className="text-[13px] leading-relaxed text-[#F5EFE4]/85 max-w-xs">
              We are three people, one long conversation with light. Every
              fixture leaves the studio with a signature and a warm-up curve.
            </p>
            <div className="mt-6 flex items-center gap-3">
              {socials.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  aria-label={social.label}
                  className="w-9 h-9 flex items-center justify-center rounded-full border border-[#F5EFE4]/30 hover:border-[#F5EFE4] hover:bg-[#F5EFE4]/10 transition-colors cursor-pointer"
                >
                  <i className={`${social.icon} text-[14px] text-[#F5EFE4]`} />
                </a>
              ))}
            </div>
          </div>

          {columns.map((col) => (
            <div key={col.title}>
              <div className="text-[10px] uppercase tracking-[0.4em] text-[#F5EFE4]/70 mb-5">
                {col.title}
              </div>
              <ul className="space-y-3">
                {col.links.map((link) => (
                  <li key={link.label}>
                    <a
                      href={link.href}
                      className="text-[14px] text-[#F5EFE4] hover:text-white hover:underline underline-offset-4 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom bar */}
        <div className="border-t border-[#F5EFE4]/25 pt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 text-[11px] text-[#F5EFE4]/70">
          <div className="flex items-center gap-3">
            <span>© {currentYear} Elin Vahr Studio</span>
            <span className="text-[#F5EFE4]/40">·</span>
            <span>All fixtures made in small batches.</span>
          </div>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            <a href="#" className="hover:text-white cursor-pointer whitespace-nowrap">
              Privacy
            </a>
            <a href="#" className="hover:text-white cursor-pointer whitespace-nowrap">
              Terms
            </a>
            <a href="#" className="hover:text-white cursor-pointer whitespace-nowrap">
              Colophon
            </a>
            <span className="text-[#F5EFE4]/50">
              Designed with graphite, coffee & warm dimming.
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
}